import {
    selectSet,
    deleteUpload,
    uploadFlight,
    stageFiles,
    setSelectedUpload,
    selectSelectedUpload,
    selectOrthoJobByFlightId,
    selectUploadsForSetSummary,
    setOrthoOverlapLayer,
    selectOrthoOverlapLayerIsVisible,
    selectUploadIdsForSet,
    selectCancelledUploadIdsForSet,
} from "./OrthomosaicSlice"
import {
    FileUploadCategory,
    supportedFileUploadExtensions,
} from "features/job-management/uploader/constants"
import { routeCsvFiles } from "features/job-management/uploader/mma-telemetry"
import { useProcessing } from "./useOrthomosaicProcessing"
import { OrthoUploadFlowWarnings, orthoWarningDefinitions } from "./OrthomosaicWarnings"
import { GenericAddFiles } from "panels/content/side-panel/upload-flow/GenericAddFiles"
import { GenericProcessingFiles } from "panels/content/side-panel/upload-flow/GenericProcessingFiles"
import { deleteOrthoFlight, updateOrthoFlight } from "features/pubsub/entityActions/flights/ortho"
import {
    ImagePreviewType,
    JobTypes,
    SampleDataFlightKey,
    UploadComponentProps,
} from "@nova/constants"
import {
    selectIsOrthoFlightUploading,
    selectOrthoFinishedUploadSize,
    selectOrthoImageById,
    selectOrthoImagesAndUploadsWithContextByFlightId,
    selectOrthoUploadSize,
    selectSummaryFromOrthoUploads,
} from "features/entity-caching/images/orthos/selectors"
import { deleteOrthoImage } from "features/pubsub/entityActions/images/ortho"
import {
    CreateUploadFlow,
    CreateUploadFlowProps,
    getGenericUploadFlowRenderComponent,
} from "panels/content/side-panel/upload-flow/UploadFlow"
import { activateSidePanelModal } from "panels/content/side-panel/thunks"
import { selectOrthoFlightById } from "features/entity-caching/flights/selectors"
import { getOrthoImageS3Key } from "panels/content/side-panel/upload-flow/constants"
import {
    CreateImagePreviewModal,
    CreateImagePreviewModalProps,
} from "panels/content/side-panel/upload-flow/preview-files/GenericImagePreviewModal"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"
import { OrthomosaicPreviewFiles } from "./OrthomosaicPreviewFiles"

// MMA telemetry CSVs are routed through the side-channel so stageFiles can
// dispatch them with the correct flightId; image files pass through.
const fileFilter = (files: File[]) => Promise.resolve(routeCsvFiles(files))

const OrthoUploadFlow = CreateUploadFlow({
    selectSet,
    selectUploadIdsForSet,
    selectCancelledUploadIdsForSet,
    selectUploadsForSet: selectOrthoImagesAndUploadsWithContextByFlightId,
    selectUploadsForSetSummary: selectUploadsForSetSummary,
    deleteUpload,
    deleteUploadedUpload: deleteOrthoImage,
    uploadFlight,
    stageFiles,
    selectJob: selectOrthoJobByFlightId,
    setSelectedPlugin: activateSidePanelModal,
    useProcessing,
    warningData: orthoWarningDefinitions,
    minUploads: 3,
    selectSelectedUpload: selectSelectedUpload,
    setSelectedUpload,
    supportedFileUploadExtensions: supportedFileUploadExtensions[FileUploadCategory.OrthoImages],
    fileFilter,
    deleteFlight: deleteOrthoFlight,
    updateFlight: updateOrthoFlight,
    selectFlightById: selectOrthoFlightById,
    jobProgressType: JobTypes.Ortho,
    getS3Key: getOrthoImageS3Key,
    selectIsUploading: selectIsOrthoFlightUploading,
    selectUploadSize: selectOrthoUploadSize,
    selectFinishedUploadSize: selectOrthoFinishedUploadSize,
    selectUploadStateSummary: selectSummaryFromOrthoUploads,
    uploadFlowWarnings: OrthoUploadFlowWarnings,
    setOverlapLayer: setOrthoOverlapLayer,
    selectOverlapLayerIsEnabled: selectOrthoOverlapLayerIsVisible,
    imagePreviewType: ImagePreviewType.Ortho,
    pluginImageUrl: "/images/plugins/layers/ortho.svg",
    pluginId: NovaPluginIds.Ortho,
    sampleDataFlightKey: SampleDataFlightKey.ortho2,
} as CreateUploadFlowProps)

const OrthoRenderComponent = getGenericUploadFlowRenderComponent({
    AddFiles: GenericAddFiles,
    PreviewFiles: OrthomosaicPreviewFiles,
    ProcessingFiles: GenericProcessingFiles,
})

export const OrthoMultifileUpload = (props: UploadComponentProps) => {
    return (
        <OrthoUploadFlow {...props}>
            <OrthoRenderComponent />
        </OrthoUploadFlow>
    )
}

export const OrthoImagePreviewModal = CreateImagePreviewModal({
    imagePreviewType: ImagePreviewType.Ortho,
    selectUploadsForSet: selectOrthoImagesAndUploadsWithContextByFlightId,
    deleteUpload: deleteUpload,
    deleteUploadedUpload: deleteOrthoImage,
    selectImageById: selectOrthoImageById,
    selectJobByFlightId: selectOrthoJobByFlightId,
    selectFlightById: selectOrthoFlightById,
} satisfies CreateImagePreviewModalProps)
